import { useParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import {
  ArrowLeft,
  BookOpen,
  Scroll as ScrollIcon,
  User,
  Calendar,
  MapPin,
  Globe,
  Tag,
  Hash,
  Info,
  Mail,
  FileText,
  Layers,
} from "lucide-react";
import { catalogItems } from "@jambushrushti/data/libraryData";

const LibraryItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const item = catalogItems.find(i => i.id === Number(id));

  // Get similar items (same type or category, excluding current)
  const similarItems = item
    ? catalogItems
        .filter(
          i =>
            (i.type === item.type || i.category === item.category) &&
            i.id !== item.id
        )
        .slice(0, 4)
    : [];

  if (!item) {
    return (
      <Layout>
        <section className="bg-background py-20">
          <div className="container mx-auto px-4 text-center">
            <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
            <h1 className="font-display mb-4 text-2xl font-bold text-foreground">
              Item Not Found
            </h1>
            <p className="mb-6 text-muted-foreground">
              The item you're looking for doesn't exist or has been removed.
            </p>
            <Button variant="hero" asChild>
              <Link to="/library">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Library
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  const ItemIcon = item.type === "Book" ? BookOpen : ScrollIcon;
  const isManuscript = item.type === "Manuscript";

  return (
    <Layout>
      {/* Breadcrumb */}
      <PageBreadcrumb
        items={[{ label: "Library", href: "/library" }, { label: item.title }]}
      />

      {/* Item Details */}
      <section className="bg-background py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            {/* Item Visual */}
            <div className="group relative">
              <div className="shadow-elevated mx-auto aspect-[3/4] max-w-md overflow-hidden rounded-2xl bg-secondary/30 lg:mx-0">
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              {/* Availability Badge */}
              <Badge
                className={`absolute right-4 top-4 ${item.available ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
              >
                {item.available ? "Available" : "Reference Only"}
              </Badge>
            </div>

            {/* Item Info */}
            <div className="animate-fade-up space-y-6">
              <div>
                <Badge variant="outline" className="mb-3">
                  <ItemIcon className="mr-1 h-3 w-3" />
                  {item.type}
                </Badge>
                <h1 className="font-display mb-4 text-3xl font-bold text-foreground lg:text-4xl">
                  {item.title}
                </h1>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm">
                      <span className="font-medium text-foreground">
                        Author:
                      </span>{" "}
                      {item.author}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span className="text-sm">
                      <span className="font-medium text-foreground">Year:</span>{" "}
                      {item.year}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-sm">
                      <span className="font-medium text-foreground">
                        Location:
                      </span>{" "}
                      {item.location}
                    </span>
                  </div>

                  {/* Manuscript-specific fields */}
                  {isManuscript && (
                    <>
                      {item.granthName && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Granth Name:
                            </span>{" "}
                            {item.granthName}
                          </span>
                        </div>
                      )}
                      {item.hastrapNo && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Hash className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Hastrap No:
                            </span>{" "}
                            {item.hastrapNo}
                          </span>
                        </div>
                      )}
                      {item.mulkarta && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <User className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Mulkarta:
                            </span>{" "}
                            {item.mulkarta}
                          </span>
                        </div>
                      )}
                      {item.tikakarta && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <User className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Tikakarta:
                            </span>{" "}
                            {item.tikakarta}
                          </span>
                        </div>
                      )}
                      {item.folioNo && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Layers className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Folio No:
                            </span>{" "}
                            {item.folioNo}
                          </span>
                        </div>
                      )}
                      {item.totalPages && item.availablePages && (
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <FileText className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            <span className="font-medium text-foreground">
                              Pages:
                            </span>{" "}
                            {item.availablePages} / {item.totalPages} available
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Contact Button */}
              <Button
                variant="hero"
                size="lg"
                asChild
                className="w-full sm:w-auto"
              >
                <Link to="/contact">
                  <Mail className="mr-2 h-4 w-4" />
                  Contact Librarian
                </Link>
              </Button>

              {/* Meta Info */}
              <div className="flex flex-wrap gap-4 border-t border-border pt-4">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Category:
                  </span>
                  <Badge variant="secondary">{item.category}</Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Language:
                  </span>
                  <Badge variant="outline">{item.language}</Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Catalog ID:
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {item.id}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="bg-card py-10">
        <div className="container mx-auto px-4">
          <h2 className="font-display mb-4 text-xl font-bold text-primary">
            Description
          </h2>
          <p className="max-w-3xl leading-relaxed text-muted-foreground">
            {item.description}
          </p>

          {/* Additional Info */}
          <div className="mt-6 grid max-w-lg gap-4 border-t border-border pt-6 sm:grid-cols-2">
            <div>
              <span className="text-sm text-muted-foreground">Publisher:</span>
              <p className="text-sm font-medium text-foreground">
                {item.publisher}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Pages:</span>
              <p className="text-sm font-medium text-foreground">
                {item.pages}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Notice */}
      <section className="border-y border-border bg-background py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-start gap-4 rounded-xl bg-secondary/50 p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="mb-1 text-sm font-medium text-foreground">
                {isManuscript
                  ? "Manuscript Access"
                  : "Physical Library Access Only"}
              </p>
              <p className="text-sm text-muted-foreground">
                {isManuscript
                  ? "This manuscript is part of our special collection. Please contact the librarian to request supervised access for research purposes."
                  : "This item is available at our physical library. Please contact the librarian to schedule a visit or request access."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Items */}
      {similarItems.length > 0 && (
        <section className="bg-gradient-spiritual relative overflow-hidden py-16">
          <div className="lotus-pattern absolute inset-0 opacity-30" />

          <div className="container relative z-10 mx-auto px-4">
            <div className="mb-10 text-center">
              <span className="text-xs font-medium uppercase tracking-widest text-primary">
                Explore More
              </span>
              <h2 className="font-display mt-2 text-2xl font-bold text-foreground lg:text-3xl">
                Similar Items
              </h2>
            </div>

            <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {similarItems.map((similarItem, index) => {
                const SimilarIcon =
                  similarItem.type === "Book" ? BookOpen : ScrollIcon;
                return (
                  <Link
                    key={similarItem.id}
                    to={`/library/${similarItem.id}`}
                    className="group"
                  >
                    <Card
                      variant="interactive"
                      className="animate-fade-up h-full overflow-hidden bg-card"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="aspect-[3/4] overflow-hidden bg-secondary/30">
                        <img
                          src={similarItem.image}
                          alt={similarItem.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <SimilarIcon className="mr-1 h-3 w-3" />
                            {similarItem.type}
                          </Badge>
                        </div>
                        <h3 className="font-display line-clamp-2 text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                          {similarItem.title}
                        </h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {similarItem.author}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="border-t border-border bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4 text-muted-foreground">
            Explore our complete collection of books and manuscripts
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="hero" asChild>
              <Link to="/library">
                <BookOpen className="mr-2 h-4 w-4" />
                Browse Library Catalog
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/contact">Contact Librarian</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LibraryItemDetails;
